import { Visibility, FilterList, Business } from '@mui/icons-material';
import { CardContainer, DataTable, Dropdown, Tag } from 'finara-components';
import type { DataTableColumn } from 'finara-components';
import { useState, useEffect } from 'react';

import { CsvExportButton } from '@/components/common';
import { useCredits } from '@/hooks/queries/useCredits';
import { useOrganizations } from '@/hooks/queries/useOrganizations';
import { creditsService } from '@/services/apiClient';
import { Credit, CreditFilters, CreditStatus } from '@/types/credits';

import { CreditDetailModal } from './CreditDetailModal';

interface AdvanceRequestsProps {
  organizationId?: string;
}

export const AdvanceRequests: React.FC<AdvanceRequestsProps> = ({ organizationId }) => {
  const [filters, setFilters] = useState<CreditFilters>({
    page: 1,
    limit: 10,
    orderDirection: 'DESC',
    ...(organizationId && { organization_id: organizationId }),
  });
  const [enrichedCredits, setEnrichedCredits] = useState<Credit[]>([]);
  const [isEnriching, setIsEnriching] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState<Credit | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch data
  const { data: creditsData, isLoading, error, refetch } = useCredits(filters);
  const { data: organizationsData } = useOrganizations(1, 100); // Get all organizations for filter

  // Enrich credits with detailed information (user and organization names)
  useEffect(() => {
    const enrichCreditsData = async () => {
      if (!creditsData?.data || creditsData.data.length === 0) {
        setEnrichedCredits([]);
        return;
      }

      setIsEnriching(true);
      try {
        // Fetch detailed information for each credit
        const enrichedCreditsPromises = creditsData.data.map(async credit => {
          try {
            const detailedCredit = await creditsService.getCredit(credit.id);
            return detailedCredit;
          } catch (_error) {
            // If fetching detailed info fails, return the original credit
            // Failed to fetch details for credit, will fallback to original data
            return credit;
          }
        });

        const enrichedCreditsResults = await Promise.all(enrichedCreditsPromises);
        setEnrichedCredits(enrichedCreditsResults);
      } catch (_error) {
        // Error enriching credits data, using fallback
        // Fallback to original data if enrichment fails
        setEnrichedCredits(creditsData.data);
      } finally {
        setIsEnriching(false);
      }
    };

    void enrichCreditsData();
  }, [creditsData?.data]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const getStatusVariant = (status: CreditStatus): 'green' | 'yellow' | 'red' | 'blue' | 'gray' => {
    switch (status) {
      case 'active':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'rejected':
        return 'red';
      case 'expired':
        return 'gray';
      case 'closed':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const handleFilterChange = (field: keyof CreditFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleViewCredit = (credit: Credit) => {
    setSelectedCredit(credit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCredit(null);
  };

  const handleCreditUpdate = async (updatedCredit: Credit) => {
    setEnrichedCredits(prev =>
      prev.map(credit =>
        credit.id === updatedCredit.id ? { ...credit, ...updatedCredit } : credit
      )
    );
    // Refetch credits to get the latest data from the server
    await refetch();
  };

  // Define columns for DataTable
  const baseColumns: DataTableColumn<Credit>[] = [
    {
      key: 'id',
      label: 'ID / Empleado',
      render: (_, credit) => (
        <div>
          <div className="font-medium text-brand-white">
            {credit.id.split('-')[0]?.toUpperCase()}-{credit.id.split('-')[1]?.slice(0, 3)}
          </div>
          <div className="text-sm text-muted">
            {credit.user?.name ?? 'Usuario no disponible'}
          </div>
        </div>
      ),
    },
    // Only show organization column if organizationId is not provided (super admin view)
    ...(!organizationId
      ? [
          {
            key: 'organization' as const,
            label: 'Empresa',
            render: (_: unknown, credit: Credit) => (
              <div className="flex items-center">
                <Business className="text-muted mr-2" fontSize="small" />
                <div>
                  <div className="text-sm text-brand-white">
                    {credit.organization?.name ?? 'Organización no disponible'}
                  </div>
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'amount',
      label: 'Monto',
      render: (_, credit) => (
        <div>
          <div className="font-medium text-brand-white">{formatCurrency(credit.amount)}</div>
        </div>
      ),
    },
    {
      key: 'request_at',
      label: 'Fecha de solicitud',
      render: (_, credit) => (
        <div className="text-sm text-brand-white">
          {new Date(credit.request_at).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          })}
        </div>
      ),
    },
    {
      key: 'due_date',
      label: 'Fecha de vencimiento',
      render: (_, credit) => (
        <div className="text-sm text-brand-white">
          {new Date(credit.due_date).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          })}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      align: 'center',
      render: (_, credit) => (
        <Tag variant={getStatusVariant(credit.status)}>
          {statusOptions.find(option => option.value === credit.status)?.label ?? credit.status}
        </Tag>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, credit) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewCredit(credit)}
            className="text-muted hover:text-brand-white hover:bg-white/5 hover:cursor-pointer p-1 rounded-md"
            title="Ver detalles"
          >
            <Visibility fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  // Prepare organization options for filter
  const organizationOptions = [
    { value: '', label: 'Todas las empresas' },
    ...(organizationsData?.data.map(org => ({
      value: org.id,
      label: org.name,
    })) ?? []),
  ];

  const statusOptions = [
    { value: '', label: 'Todos los estatus' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'active', label: 'Activo' },
    { value: 'rejected', label: 'Rechazado' },
    { value: 'expired', label: 'Expirado' },
    { value: 'closed', label: 'Cerrado' },
  ];

  if (error) {
    return (
      <CardContainer className="p-8 text-center">
        <p className="text-red-600">
          Error al cargar las solicitudes. Vuelva a intentarlo en unos minutos.
        </p>
      </CardContainer>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <CardContainer>
        <div className="flex items-center space-x-2 mb-4">
          <FilterList className="text-muted" />
          <span className="font-medium text-brand-white">Filtrar por etapa:</span>
        </div>
        <div className="flex flex-col md:!flex-row items-center gap-4">
          {/* Only show organization filter if organizationId is not provided (super admin view) */}
          {!organizationId && (
            <Dropdown
              value={filters.organization_id ?? ''}
              onChange={value => handleFilterChange('organization_id', value as string)}
              options={organizationOptions}
              placeholder="Todas las empresas"
              className="min-w-[200px] w-full lg:!w-auto"
              searchable
            />
          )}
          <Dropdown
            value={filters.status ?? ''}
            onChange={value => handleFilterChange('status', value as string)}
            options={statusOptions}
            placeholder="Todos los estatus"
            className="min-w-[200px] w-full lg:!w-auto"
          />
        </div>
      </CardContainer>

      {/* Table */}
      <CardContainer>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-brand-white">
            Solicitudes de Adelanto ({creditsData?.count ?? 0})
          </h3>
          <CsvExportButton
            data={enrichedCredits}
            filename="solicitudes_adelanto"
            columns={credit => ({
              ID: `${credit.id.split('-')[0]?.toUpperCase()}-${credit.id.split('-')[1]?.slice(0, 3)}`,
              Empleado: credit.user?.name ?? 'Usuario no disponible',
              ...(organizationId ? {} : { Empresa: credit.organization?.name ?? 'N/A' }),
              Monto: formatCurrency(credit.amount).replace(/\s/g, ''),
              'Fecha de solicitud': new Date(credit.request_at).toLocaleDateString('es-MX'),
              'Fecha de vencimiento': new Date(credit.due_date).toLocaleDateString('es-MX'),
              Estado:
                statusOptions.find(option => option.value === credit.status)?.label ??
                credit.status,
            })}
            variant="white"
            size="small"
            buttonText="Exportar"
          />
        </div>
        <div className="p-6">
          <DataTable
            columns={baseColumns}
            data={enrichedCredits}
            loading={Boolean(isLoading) || Boolean(isEnriching)}
            responsive
            emptyMessage={
              <div className="py-8 text-center">
                <Business className="text-muted mx-auto mb-4" style={{ fontSize: '3rem' }} />
                <h3 className="text-lg font-medium text-brand-white mb-2">
                  No se encontraron solicitudes
                </h3>
                <p className="text-muted">Ajusta los filtros para ver más resultados.</p>
              </div>
            }
          />
        </div>
      </CardContainer>

      {/* Credit Detail Modal */}
      <CreditDetailModal
        credit={selectedCredit}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpdate={() => void handleCreditUpdate(selectedCredit as Credit)}
      />
    </div>
  );
};
